<template>
    <div id="media-library" tabindex="-1" @keydown.a.stop="selectAll">
        <MediaLibraryClientSelect
            v-if="!clientId"
            display="card"
            :client-id="clientId"
            @clientSelected="selectClient"
        />

        <template v-else>
            <div v-if="clientHasGenerativeAiFeaturesEnabled" class="media-library__action-tabs">
                <span
                    :class="{ active: currentLibraryTab === LIBRARY_SECTION.IMPORT }"
                    @click="changeLibrarySection(LIBRARY_SECTION.IMPORT)"
                >
                    DAM Content
                </span>

                <span
                    :class="{ active: currentLibraryTab === LIBRARY_SECTION.IMAGINE }"
                    @click="changeLibrarySection(LIBRARY_SECTION.IMAGINE)"
                >
                    AI Content
                </span>

                <span
                    v-if="isVeevaIntegrationEnabled"
                    :class="{ active: currentLibraryTab === LIBRARY_SECTION.VEEVA }"
                    @click="changeLibrarySection(LIBRARY_SECTION.VEEVA)"
                >
                    Veeva
                </span>
            </div>

            <VeevaSection
                v-if="isVeevaIntegrationEnabled && currentLibraryTab === LIBRARY_SECTION.VEEVA"
                :client-id="clientId"
                :folder-id="folderId"
                @assetAdded="assetAddedFromVeeva"
            />

            <template v-else>
                <div v-show="currentLibraryTab === LIBRARY_SECTION.IMPORT" id="media-library__nav-wrapper">
                    <div v-if="sandboxMode">
                        <Button @click="selectClient(null)">Select Client</Button>
                        <span class="media-library__nav-wrapper--client-name">{{ clientName }}</span>
                    </div>

                    <Breadcrumb id="media-library__breadcrumbs" separator=">">
                        <BreadcrumbItem class="home-icon">
                            <a
                                v-if="activeFolderName !== '' && activeFolderName !== $route.params.clientId"
                                @click="goToFolder(root)"
                            >
                                <Icon type="ios-home-outline"></Icon>
                            </a>
                            <span v-else><Icon type="ios-home-outline"></Icon></span>
                        </BreadcrumbItem>
                        <BreadcrumbItem v-for="(breadcrumb, index) in breadcrumbs" :key="`breadcrumb-${index}`">
                            <a v-if="index < breadcrumbs.length - 1" @click="goToFolder(breadcrumb.id)">
                                {{ breadcrumb.name }}
                            </a>
                            <span v-else>{{ breadcrumb.name }}</span>
                        </BreadcrumbItem>
                    </Breadcrumb>

                    <div class="media-library__sort">
                        <SortMediaLibrary v-model="sortBy" />
                    </div>

                    <div id="media-library__change-view">
                        <span>{{ itemTotalText }}</span>

                        <ButtonGroup size="small" type="text" ghost>
                            <Button
                                icon="ios-grid-outline"
                                data-testid="media-library--grid-view"
                                :disabled="showGridView"
                                @click="setShowGridView"
                            />
                            <Button
                                icon="ios-list"
                                data-testid="media-library--list-view"
                                :disabled="!showGridView"
                                @click="setShowListView"
                            />
                        </ButtonGroup>
                    </div>
                </div>

                <div id="media-library__content-wrapper">
                    <div v-if="canManageMediaItems" id="media-library__sidebar">
                        <div v-if="canManageMediaItems" id="media-library__upload-area">
                            <div
                                v-show="currentLibraryTab === LIBRARY_SECTION.IMPORT"
                                class="media-library__upload-content"
                            >
                                <Button
                                    class="media-library-modal-footer__button--sidebar"
                                    data-testid="content-library-popup__create-folder-button"
                                    @click="showAddFolderModal = true"
                                >
                                    Create New Folder
                                </Button>

                                <Button
                                    v-if="showAddAssetFromLinkButton"
                                    class="media-library-modal-footer__button--sidebar"
                                    data-testid="content-library-popup__add-asset-button"
                                    @click="toggleAddAssetFromUrlModal"
                                >
                                    Add Asset from Link
                                </Button>

                                <Button
                                    class="media-library-modal-footer__button--sidebar"
                                    data-testid="content-library-popup__download-folder-button"
                                    :disabled="!folderId"
                                    @click="downloadCurrentFolder"
                                >
                                    Download Folder
                                </Button>

                                <Upload
                                    v-if="uploadConfig.action"
                                    class="media-library__upload"
                                    data-testid="content-library-popup__upload-area"
                                    :show-upload-list="false"
                                    :action="uploadConfig.action"
                                    :data="uploadConfig.data"
                                    :multiple="true"
                                    type="drag"
                                    :before-upload="beforeUpload"
                                    :on-success="handleSuccess"
                                    :on-progress="uploadProgress"
                                >
                                    <div class="media-library__upload-info">
                                        <Icon
                                            type="ios-cloud-upload-outline"
                                            class="media-library__upload-info--icon"
                                        />
                                        <div class="media-library__upload-info--text">
                                            Drop files here or
                                            <a>click to upload</a>
                                        </div>
                                    </div>
                                </Upload>
                                <div v-else class="media-library__upload-area-placeholder" />
                            </div>

                            <div
                                v-show="
                                    clientHasGenerativeAiFeaturesEnabled &&
                                    currentLibraryTab === LIBRARY_SECTION.IMAGINE
                                "
                                class="media-library__imagine-content"
                            >
                                <div class="advanced-model-options__input">
                                    <label>Prompt</label>
                                    <Input
                                        v-model="prompt"
                                        type="textarea"
                                        placeholder="Describe a scene in as much detail as possible"
                                    />
                                </div>

                                <div class="advanced-model-options__input">
                                    <div class="advanced-model-options__input-actions">
                                        <span class="link-like" @click="toggleGenerateImagesAdvanced">
                                            Show advanced options
                                            <Icon type="md-cog"></Icon>
                                        </span>

                                        <Button class="ivu-btn-primary" @click="onSearch">Generate</Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <transition name="slide">
                            <media-library-asset-tags v-if="showAssetMetadata" :asset="selectedAssetsArray[0]" />
                        </transition>
                    </div>

                    <div id="media-library__data" :class="{ 'full-width': !canManageMediaItems }">
                        <div
                            v-show="currentLibraryTab === LIBRARY_SECTION.IMAGINE"
                            class="media-library__imagine-results"
                        >
                            <template v-if="searches.length">
                                <Button class="media-library__imagine-results-clear" @click="clearGeneratedResults">
                                    Clear results
                                    <Icon type="md-trash"></Icon>
                                </Button>

                                <SearchResults
                                    v-for="searchOptions in searches"
                                    :key="searchOptions.searchId"
                                    :search-options="searchOptions"
                                    @useImage="onUseImage"
                                />
                            </template>

                            <div v-else>
                                <span class="media-library__imagine-no-results-title">
                                    Use the "Generate" button to create AI content.
                                </span>
                                <empty-message></empty-message>
                            </div>
                        </div>

                        <div v-show="currentLibraryTab !== LIBRARY_SECTION.IMAGINE">
                            <Row v-if="showGridView" class="media-library__data-row">
                                <Spin v-if="isLoading" fix />

                                <div
                                    v-else-if="!mediaItems.length"
                                    class="media-library__data-row-empty"
                                    data-testid="media-library__empty-folder"
                                >
                                    Folder Empty
                                </div>

                                <Col
                                    v-for="asset in mediaItemsForDisplay"
                                    v-else
                                    :key="asset.id"
                                    span="6"
                                    :data-asset="asset"
                                    :class="{ selected: selectedAssets[asset.id], 'media-library__data-col': true }"
                                    @mousedown.prevent.stop="selectAsset(asset)"
                                    @dblclick.stop="doubleClickSelectAsset(asset)"
                                >
                                    <Card
                                        :bordered="true"
                                        :body-style="{ padding: 0, width: '100%' }"
                                        class="media-library__data-card"
                                    >
                                        <div
                                            v-if="
                                                asset.type === 'FILE' &&
                                                asset.info.contentType &&
                                                asset.info.contentType.match(/^image\//) &&
                                                asset.info.contentType !== 'image/vnd.adobe.photoshop'
                                            "
                                            class="media-library__data-card-image"
                                            :style="{ 'background-image': `url(${asset.url})` }"
                                        />

                                        <video
                                            v-else-if="
                                                asset.type === 'FILE' &&
                                                asset.info.contentType &&
                                                asset.info.contentType.match(/^video\//)
                                            "
                                            width="100%"
                                            height="125"
                                            autoplay
                                            loop
                                            muted
                                        >
                                            <source :src="asset.url" :type="asset.info.contentType" />
                                        </video>

                                        <div
                                            v-else-if="asset.type === 'FILE'"
                                            class="file-format-icon media-library__data-card-icon"
                                        >
                                            <span :class="`pretty-file-icon-${fileIcon(asset.name)}`"></span>
                                        </div>

                                        <span v-else class="media-library__folder-preview-wrapper">
                                            <img
                                                v-if="asset.url"
                                                class="folder-preview media-library__data-card-image"
                                                :src="asset.url"
                                                :alt="asset.name"
                                            />

                                            <Icon
                                                v-else
                                                class="folder-preview media-library__data-card-icon"
                                                type="ios-folder-outline"
                                                size="large"
                                            ></Icon>
                                        </span>

                                        <div
                                            class="media-library__data-card-name"
                                            data-testid="media-library__data-card-name"
                                        >
                                            {{ asset.name }}
                                        </div>

                                        <Button
                                            v-if="canManageMediaItems"
                                            icon="ios-trash-outline"
                                            type="error"
                                            ghost
                                            class="media-library__data-card-delete-button"
                                            @click.stop="deleteAsset(asset)"
                                        />

                                        <Button
                                            v-if="canManageMediaItems && asset.type === MEDIA_TYPES.FOLDER"
                                            icon="md-create"
                                            ghost
                                            class="media-library__data-card-rename-button"
                                            data-testid="media-library__folder-rename-button"
                                            @click.stop="openRenameFolderModal(asset)"
                                        />

                                        <Button
                                            v-if="showCopyButton(asset.type)"
                                            icon="md-download"
                                            ghost
                                            class="media-library__data-card-download-button"
                                            data-testid="media-library__asset-download-button"
                                            @click.stop="downloadAsset(asset)"
                                        />

                                        <Button
                                            v-if="asset.type === MEDIA_TYPES.FOLDER"
                                            icon="md-download"
                                            ghost
                                            class="media-library__data-card-download-button media-library__data-card-download-button--solo"
                                            data-testid="media-library__folder-download-button"
                                            @click.stop="downloadFolder(asset)"
                                        />

                                        <Button
                                            v-if="showCopyButton(asset.type)"
                                            icon="ios-copy"
                                            ghost
                                            class="media-library__data-card-copy-button"
                                            data-testid="media-library__asset-copy-button"
                                            @click.stop="copyAssetIdToClipboard(asset)"
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Table
                                v-else
                                :columns="columns"
                                :data="mediaItems"
                                no-data-text="Folder Empty"
                                :row-class-name="rowClassName"
                                class="assetsTable"
                                @onRowClick="selectAsset"
                                @onRowDblclick="doubleClickSelectAsset"
                            />
                        </div>
                    </div>
                </div>

                <portal to="media-library-modal-footer">
                    <div class="media-library-modal-footer__content">
                        <div class="media-library-modal-footer__status">
                            <p>{{ selectedAssetsInfo }}</p>
                        </div>
                        <div>
                            <Button
                                data-testid="content-library-popup__close-button"
                                class="media-library-modal-footer__button"
                                @click="onClose"
                            >
                                Close
                            </Button>

                            <Button
                                v-if="fileSelectionIsEnabled"
                                class="media-library-modal-footer__button"
                                :disabled="!Object.keys(selectedAssets).length"
                                type="primary"
                                @click="addSelections"
                            >
                                {{ addSelectionText }}
                            </Button>
                        </div>
                    </div>
                </portal>

                <hox-modal v-if="showAddFolderModal" data-testid="add-new-folder-modal" @close="closeAddFolder">
                    <template #header>Add new folder</template>
                    <Input
                        v-model="newFolderName"
                        data-testid="add-new-folder-modal__input"
                        placeholder="New folder name"
                        @keyup.enter="addFolder"
                    />
                    <template #footer>
                        <Button @click="closeAddFolder">Cancel</Button>
                        <Button
                            :disabled="!newFolderName"
                            :loading="addFolderLoading"
                            type="primary"
                            data-testid="add-new-folder-modal-add-button"
                            @click="addFolder"
                        >
                            Add
                        </Button>
                    </template>
                </hox-modal>

                <hox-modal
                    v-if="showRenameFolderModal"
                    data-testid="rename-folder-modal"
                    @close="closeRenameFolderModal"
                >
                    <template #header>Rename folder</template>
                    <Input
                        v-model="renameFolderName"
                        data-testid="rename-folder-modal__input"
                        placeholder="Folder name"
                        @keyup.enter="renameFolder"
                    />
                    <template #footer>
                        <Button @click="closeRenameFolderModal">Cancel</Button>
                        <Button
                            :disabled="!renameFolderName.trim()"
                            :loading="renameFolderLoading"
                            type="primary"
                            data-testid="rename-folder-modal-button"
                            @click="renameFolder"
                        >
                            Rename
                        </Button>
                    </template>
                </hox-modal>

                <AddAssetFromUrlModal
                    v-if="addAssetFromUrlModalIsVisible"
                    :folder-id="folderId"
                    :media-items="mediaItems"
                    :external-asset-url="aIassetUrl"
                    @assetAdded="assetAddedFromUrl"
                    @close="toggleAddAssetFromUrlModal"
                />

                <hox-modal v-if="generateImagesAdvancedVisible" @close="toggleGenerateImagesAdvanced">
                    <template #header>DALL·E 3 advanced options</template>

                    <AdvancedDalleOptions
                        :custom-inputs="computedCustomInputs"
                        @updatedCustomInputs="onUpdateCustomInputs"
                    />
                </hox-modal>
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent, resolveComponent, h as vueH } from "vue";
import { createNamespacedHelpers } from "vuex";
import type { Client } from "hoxton-types";
import { EditableImageTypes } from "hoxton-shared/enums/editables";
import slugify from "hoxton-shared/utils/slugify";
import { ZIP_MIME_TYPE } from "hoxton-shared/enums/mimeTypes";

import type { MastheadUploadConfig, Asset } from "@/types/types";
import * as prettyFileIcons from "@/packages/pretty-file-icons";

import bus from "@/bus";

import mediaItemsQuery from "@/apollo/queries/MediaItems.gql";
import createMediaFolderMutation from "@/apollo/mutations/CreateMediaFolder.gql";
import uploadMediaFilesMutation from "@/apollo/mutations/UploadMediaFiles.gql";
import uploadAndExtractZipToFolderMutation from "@/apollo/mutations/UploadAndExtractZipToFolder.gql";
import updateMediaFilesMutation from "@/apollo/mutations/UpdateMediaFiles.gql";
import renameMediaFolder from "@/apollo/mutations/RenameMediaFolder.gql";
import removeMediaMutation from "@/apollo/mutations/RemoveMedia.gql";
import downloadMediaFolderMutation from "@/apollo/mutations/DownloadMediaFolder.gql";

import AddAssetFromUrlModal from "@/components/MediaLibraryAddAssetFromUrlModal.vue";
import SortMediaLibrary from "@/components/SortMediaLibrary.vue";
import MediaLibraryAssetTags from "@/components/MediaLibraryAssetTags.vue";
import MediaLibraryClientSelect from "@/components/MediaLibraryClientSelect.vue";
import EmptyMessage from "@/components/common/EmptyMessage.vue";
import SearchResults from "@/components/AI/SearchResults.vue";
import AdvancedDalleOptions from "@/components/AI/AdvancedDalleOptions.vue";
import HoxModal from "@/components/Modal/Modal/Modal.vue";
import { defaultCustomInputs, type CustomInputs } from "@/components/AI/AdvancedDalleOptions.vue";
import VeevaSection from "@/components/MediaLibrary/VeevaSection.vue";

import {
    MEDIA_TYPES,
    IMAGE_PROCESSING_MIMES,
    IMAGE_PROCESSING_EXTS,
    MediaLibraryAssetEvent
} from "@/enums/mediaLibrary.ts";

import { AuthGetters } from "@/store/modules/auth";
import { bytesToSize, formatTimestamp, getObjectValue, splitFileNameAndExtension, uploadConfigExpired } from "@/utils";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";
import { LIBRARY_SECTION, type LibrarySection } from "@/components/MediaLibrary/consts";
import copyToClipboard from "@/utils/copyToClipboard";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

const { mapState, mapGetters, mapMutations, mapActions } = createNamespacedHelpers("mediaLibrary");

type SearchConfig = {
    searchId: number;
    prompt: string;
    model: string;
    customInputs?: CustomInputs;
};

export default defineComponent({
    components: {
        HoxModal,
        AddAssetFromUrlModal,
        EmptyMessage,
        SortMediaLibrary,
        SearchResults,
        MediaLibraryAssetTags,
        MediaLibraryClientSelect,
        AdvancedDalleOptions,
        VeevaSection
    },

    setup() {
        const ga4 = useGA4Tracking();
        return { ga4 };
    },

    data() {
        return {
            MEDIA_TYPES,
            LIBRARY_SECTION,
            addAssetFromUrlModalIsVisible: false,
            generateImagesAdvancedVisible: false,
            addFolderLoading: false,
            showRenameFolderModal: false,
            renameFolderLoading: false,
            renameFolderName: "",
            folderToRename: null as Asset | null,
            currentLibraryTab: LIBRARY_SECTION.IMPORT as LibrarySection,
            columns: [
                {
                    title: "Name",
                    key: "name",
                    className: "media-library__asset-name-column",
                    render: (h: typeof vueH, params: { row: Asset }) => {
                        const Icon = resolveComponent("Icon");
                        const Button = resolveComponent("Button");

                        const elements = [
                            h(Icon, {
                                class: { "media-library__asset-icon": true },
                                type:
                                    params.row.type.toLowerCase() === "file"
                                        ? "ios-document-outline"
                                        : "ios-folder-outline"
                            }),
                            h("span", params.row.name)
                        ];

                        if (params.row.type === MEDIA_TYPES.FILE) {
                            elements.push(
                                h(Button, {
                                    class: {
                                        "media-library__asset-download-button": true,
                                        "media-library__asset-download-button--solo": !this.canManageMediaItems
                                    },
                                    icon: "md-download",
                                    ghost: true,
                                    size: "small",
                                    onClick: () => {
                                        this.downloadAsset(params.row);
                                    }
                                })
                            );
                        } else if (params.row.type === MEDIA_TYPES.FOLDER) {
                            elements.push(
                                h(Button, {
                                    class: {
                                        "media-library__asset-download-button": true,
                                        "media-library__asset-download-button--solo": !this.canManageMediaItems
                                    },
                                    icon: "md-download",
                                    ghost: true,
                                    size: "small",
                                    onClick: () => {
                                        this.downloadFolder(params.row);
                                    }
                                })
                            );
                        }

                        if (this.canManageMediaItems) {
                            elements.push(
                                h(Button, {
                                    class: { "media-library__asset-delete-button": true },
                                    icon: "ios-trash-outline",
                                    type: "error",
                                    ghost: true,
                                    size: "small",
                                    onClick: () => {
                                        this.deleteAsset(params.row);
                                    }
                                })
                            );

                            if (params.row.type === MEDIA_TYPES.FOLDER) {
                                elements.push(
                                    h(Button, {
                                        class: { "media-library__asset-rename-button": true },
                                        icon: "md-create",
                                        ghost: true,
                                        size: "small",
                                        onClick: () => {
                                            this.openRenameFolderModal(params.row);
                                        }
                                    })
                                );
                            }
                        }

                        return h("span", elements);
                    }
                },
                {
                    title: "Last modified",
                    key: "modified",
                    render: (h: typeof vueH, params: { row: Asset }) =>
                        h("span", formatTimestamp(params.row.info.modified))
                },
                {
                    title: "Created by",
                    key: "createdBy",
                    render: (h: typeof vueH, params: { row: Asset }) => h("span", params.row.info.createdBy)
                },
                {
                    title: "Created",
                    key: "created",
                    render: (h: typeof vueH, params: { row: Asset }) =>
                        h("span", formatTimestamp(params.row.info.created))
                }
            ],
            aIassetUrl: "",
            mediaItems: [] as Asset[],
            mediaItemsForDisplay: [] as Asset[],
            model: "dalle3",
            breadcrumbs: [] as { name: string; id: string }[],
            selectedAssets: {} as Record<string, Asset>,
            root: null as string | null,
            showAddFolderModal: false,
            isLoading: false,
            processing: 0,
            loadPercent: 0,
            newFolderName: "",
            selectActiveFileOnMediaItemsLoad: true,
            uploadConfig: {} as { action: string; data: Record<string, unknown>; filePrefix: string },
            extractList: {} as Record<string, { tempPath: string; fileId?: string }>,
            uploadList: {} as Record<string, string>,
            updateList: {} as Record<string, { tempPath: string; fileId?: string }>,
            prompt: "",
            searchCount: 0,
            searches: [] as SearchConfig[],
            customInputs: { ...defaultCustomInputs } as Partial<CustomInputs>
        };
    },

    computed: {
        ...mapState(["activeFile", "activeFolderId", "showGridView", "activeInstance"]),

        ...mapGetters(["activeFolderName"]),

        isWppLogin() {
            return this.$store.state.auth.isWppLogin;
        },

        isVeevaIntegrationEnabled() {
            // we can add more filters (e.g. client) here if needed
            return this.isWppLogin;
        },

        canListMediaItems() {
            return this.$auth.userCan(this.$auth.Actions.CanListMediaItems);
        },

        canManageMediaItems() {
            if (this.sandboxMode) {
                return false;
            }
            return this.$auth.userCan(this.$auth.Actions.CanManageMediaItems);
        },

        clientHasGenerativeAiFeaturesEnabled() {
            if (this.$store.state.ui.currentClient) {
                return (
                    this.$store.state.ui.currentClient.imageProcessingEnabled &&
                    this.$store.getters[AuthGetters.isSuperAdmin]
                );
            }

            return false;
        },

        clientId() {
            if (this.sandboxMode) {
                return this.sandboxSelectedClient._id;
            }

            return this.clientIdFromRoute;
        },

        clientName() {
            if (this.sandboxMode) {
                return this.sandboxSelectedClient.name;
            }
            return "";
        },

        computedCustomInputs(): CustomInputs {
            return {
                size: this.customInputs.size ?? defaultCustomInputs.size
            };
        },

        folderId() {
            return this.activeFolderId || this.root;
        },

        sandboxMode() {
            return !this.clientIdFromRoute;
        },

        itemTotalText() {
            return `${this.mediaItems.length} item${this.mediaItems.length !== 1 ? "s" : ""}`;
        },

        isOneImageSelected() {
            const assets = Object.values(this.selectedAssets);
            if (assets.length !== 1) {
                return false;
            }
            const [selectedAsset] = assets;
            const { info: { contentType } = {} } = selectedAsset;
            if (contentType) {
                return IMAGE_PROCESSING_MIMES.some(mime => mime === contentType);
            }

            return IMAGE_PROCESSING_EXTS.some(ext => selectedAsset.persistentKey.endsWith(ext));
        },

        clientIdFromRoute() {
            return this.$route.params.clientId as string;
        },

        selectedAssetsInfo() {
            const selectedAssets = Object.keys(this.selectedAssets);

            if (!selectedAssets.length) {
                return "";
            }
            if (selectedAssets.length > 1) {
                return `${selectedAssets.length} assets selected`;
            }

            const info = [];

            if (this.selectedAssets[selectedAssets[0]].name) {
                info.push(`Name: ${this.selectedAssets[selectedAssets[0]].name}`);
            }

            return info.concat(this.selectedAssetInfo).join(" | ");
        },

        selectedAssetInfo() {
            const info = [];
            const selectedAssets = Object.keys(this.selectedAssets);

            if (this.selectedAssets[selectedAssets[0]].info.contentType) {
                info.push(`Kind: ${this.selectedAssets[selectedAssets[0]].info.contentType}`);
            }
            if (this.selectedAssets[selectedAssets[0]].info.contentLength) {
                info.push(`Size: ${bytesToSize(this.selectedAssets[selectedAssets[0]].info.contentLength)}`);
            }
            if (this.selectedAssets[selectedAssets[0]].info.createdBy) {
                info.push(`Created by: ${this.selectedAssets[selectedAssets[0]].info.createdBy}`);
            }
            if (this.selectedAssets[selectedAssets[0]].info.modified) {
                info.push(`Modified: ${formatTimestamp(this.selectedAssets[selectedAssets[0]].info.modified)}`);
            }
            return info.join(" | ");
        },

        showAssetMetadata() {
            return (
                this.clientHasGenerativeAiFeaturesEnabled &&
                this.isOneImageSelected &&
                this.currentLibraryTab === LIBRARY_SECTION.IMPORT
            );
        },

        selectedAssetsArray() {
            return Object.values(this.selectedAssets);
        },

        addSelectionText() {
            return `Add Selection${Object.keys(this.selectedAssets).length > 1 ? "s" : ""}`;
        },

        fileSelectionIsEnabled() {
            return this.activeInstance !== null && this.activeInstance !== undefined;
        },

        isSelectingImage() {
            return (
                this.$store.state.mediaLibrary.activeInstance &&
                EditableImageTypes.includes(this.$store.state.mediaLibrary.activeInstance.type)
            );
        },

        sandboxSelectedClient(): { _id?: string; name?: string } {
            return this.$store.state.mediaLibrary.sandboxSelectedClient || {};
        },

        showAddAssetFromLinkButton() {
            return this.$store.state.mediaLibrary.activeInstance === null || this.isSelectingImage;
        },

        sortBy: {
            get() {
                return this.$store.state.mediaLibrary.sortBy;
            },
            set(sortBy: string) {
                this.$store.dispatch(MediaLibraryAction.SetSortBy, sortBy);
            }
        }
    },

    watch: {
        canListMediaItems() {
            if (!this.canListMediaItems) {
                this.$snackbar.warning("You do not have access to list media items");
                this.close();
            }
        },

        mediaItems: {
            handler(newVal: Asset[], oldVal: Asset[]) {
                if (this.activeFile && this.selectActiveFileOnMediaItemsLoad) {
                    const activeAsset = this.mediaItems.find(mediaItem => mediaItem.id === this.activeFile);
                    if (activeAsset && !this.selectedAssets[activeAsset.id]) {
                        this.selectAsset(activeAsset);
                    }
                    this.selectActiveFileOnMediaItemsLoad = false;
                }
                if (newVal.length && oldVal.length) {
                    const oldIds = oldVal.map(({ id }) => id);
                    const newIds = newVal.map(({ id }) => id);
                    if (newIds.every(id => oldIds.includes(id))) {
                        return;
                    }
                }

                this.mediaItemsForDisplay = this.mediaItems;
            },
            deep: true
        },

        $route() {
            // Close the media library when the route changes
            this.close();
        }
    },

    async created() {
        // make sure we have up to date data - fixes issues when switching between clients
        await this.$apollo.queries.mediaItems.refetch();
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.MEDIA_LIBRARY,
            featureContext: GA4.FEATURE_CONTEXTS.MEDIA_LIBRARY
        });
    },

    unmounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
            featureCategory: GA4.FEATURE_CATEGORIES.MEDIA_LIBRARY,
            featureContext: GA4.FEATURE_CONTEXTS.MEDIA_LIBRARY
        });
    },

    methods: {
        ...mapMutations(["setActiveFolderId", "setShowGridView", "setShowListView"]),

        ...mapActions(["close"]),

        deselectAsset(assetId: string) {
            delete this.selectedAssets[assetId];
        },

        selectAll(event: KeyboardEvent) {
            if (getObjectValue(this, "activeInstance.multiple") && (event.ctrlKey || event.metaKey)) {
                const allSelectedAssets = {} as Record<string, Asset>;
                this.mediaItems.forEach(asset => {
                    if (!this.disableSelectByType(asset)) {
                        allSelectedAssets[asset.id] = asset;
                    }
                });
                this.selectedAssets = allSelectedAssets;

                if (!this.showAddFolderModal) {
                    event.preventDefault();
                }
            }
        },

        selectAsset(asset: Asset) {
            // Only allow selection of suitable assets
            if (this.disableSelectByType(asset)) {
                return;
            }

            // Update selected assets
            if (this.selectedAssets[asset.id]) {
                this.deselectAsset(asset.id);
            } else if (this.activeInstance?.multiple) {
                this.selectedAssets[asset.id] = asset;
            } else {
                this.selectedAssets = { [asset.id]: asset };
            }
        },

        async selectClient(client: Client | null) {
            // Only reset things when the client actually changes
            if (!client || client._id !== this.sandboxSelectedClient._id) {
                this.root = null;
                this.setActiveFolderId(null);
            }
            this.$store.dispatch(MediaLibraryAction.SetSandboxSelectedClient, client);
            await this.$apollo.queries.mediaItems.refetch();
        },

        showCopyButton(assetType: (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES]) {
            return assetType === MEDIA_TYPES.FILE;
        },

        disableSelectByType(asset: Asset) {
            if (!this.activeInstance) {
                return asset.type === MEDIA_TYPES.FOLDER;
            }

            return (
                (asset.type === MEDIA_TYPES.FOLDER && this.activeInstance.type !== "folder") ||
                (asset.type === MEDIA_TYPES.FILE && this.activeInstance.type === "folder")
            );
        },

        changeLibrarySection(section: (typeof LIBRARY_SECTION)[keyof typeof LIBRARY_SECTION]) {
            this.selectedAssets = {};
            this.currentLibraryTab = section;
        },

        doubleClickSelectAsset(asset: Asset) {
            if (asset.type === "FOLDER") {
                this.selectedAssets = {};
                this.setActiveFolderId(asset.id);
            }
        },

        goToFolder(folderId: string | null) {
            this.selectedAssets = {};
            this.setActiveFolderId(folderId);
        },

        async addFolder() {
            if (this.addFolderLoading) {
                return;
            }

            if (this.newFolderName.trim() === "") {
                this.$snackbar.fieldRequired("Folder name must not be empty");
                return;
            }

            this.addFolderLoading = true;

            try {
                await this.$apollo.mutate({
                    mutation: createMediaFolderMutation,
                    variables: {
                        clientId: this.clientId,
                        folderId: this.folderId,
                        newFolderName: this.newFolderName
                    },
                    refetchQueries: [
                        {
                            query: mediaItemsQuery,
                            variables: {
                                clientId: this.clientId,
                                folderId: this.folderId,
                                orderBy: this.sortBy
                            }
                        }
                    ]
                });

                this.closeAddFolder();
                this.addFolderLoading = false;
                this.$apollo.queries.mediaItems.refetch();
            } catch (err) {
                this.$snackbar.error((err as Error).message);
                this.addFolderLoading = false;
            }
        },

        closeAddFolder() {
            this.newFolderName = "";
            this.showAddFolderModal = false;
            this.addFolderLoading = false;
        },

        openRenameFolderModal(asset: Asset) {
            this.folderToRename = asset;
            this.renameFolderName = asset.name;
            this.showRenameFolderModal = true;
        },

        closeRenameFolderModal() {
            this.renameFolderName = "";
            this.folderToRename = null;
            this.showRenameFolderModal = false;
            this.renameFolderLoading = false;
        },

        async renameFolder() {
            if (this.renameFolderLoading || !this.folderToRename) {
                return;
            }

            if (this.renameFolderName.trim() === "") {
                this.$snackbar.fieldRequired("Folder name must not be empty");
                return;
            }

            if (this.renameFolderName === this.folderToRename.name) {
                this.closeRenameFolderModal();
                return;
            }

            this.renameFolderLoading = true;

            try {
                await this.$apollo.mutate({
                    mutation: renameMediaFolder,
                    variables: {
                        clientId: this.clientId,
                        mediaFolderId: this.folderToRename.id,
                        newFolderName: this.renameFolderName
                    },
                    refetchQueries: [
                        {
                            query: mediaItemsQuery,
                            variables: {
                                clientId: this.clientId,
                                folderId: this.folderId,
                                orderBy: this.sortBy
                            }
                        }
                    ]
                });

                this.$snackbar.success(
                    `Folder ${this.folderToRename.name} renamed successfully to ${this.renameFolderName}`
                );
                this.closeRenameFolderModal();
                this.renameFolderLoading = false;
                this.$apollo.queries.mediaItems.refetch();
            } catch (err) {
                this.$snackbar.error((err as Error).message);
                this.renameFolderLoading = false;
            }
        },

        processConfig(config: MastheadUploadConfig) {
            this.uploadConfig = { ...config };
            this.uploadConfig.data = JSON.parse(this.uploadConfig.postData);
            this.uploadConfig.data.key = this.uploadConfig.filePrefix;
        },

        async handleSuccess(response, file, list) {
            const allFilesUploaded = list.filter(listFile => listFile.status !== "uploading").length === list.length;
            // Only process if all files have uploaded
            if (allFilesUploaded) {
                // Get extract list
                const extractList = Object.keys(this.extractList);
                // Process zip files to be extracted
                if (extractList.length) {
                    // we want to handle the promises one at the time.
                    await extractList.reduce(
                        (p, fileName) =>
                            p.then(async () => {
                                await this.getZipPrompt(fileName);
                            }),
                        Promise.resolve()
                    );
                }

                this.processUploads();
            }
        },

        async getZipPrompt(fileName: string) {
            return new Promise<void>(resolve => {
                this.$Modal.confirm({
                    title: "Extract Zip?",
                    content: `Would you like to extract the contents of ${fileName} on upload.`,
                    okText: "Yes",
                    cancelText: "No",
                    onOk: () => {
                        // in case of multiple zips we need to allow time for modal to hide before showing it with new values
                        setTimeout(() => {
                            resolve();
                        }, 500);
                    },
                    onCancel: () => {
                        // Zip not being extracted so add to upload or update list
                        if (this.extractList[fileName].fileId) {
                            this.updateList[fileName] = this.extractList[fileName];
                        } else {
                            this.uploadList[fileName] = this.extractList[fileName].tempPath;
                        }
                        // Extract not required so remove from like
                        delete this.extractList[fileName];
                        // Process on last zip
                        setTimeout(() => {
                            resolve();
                        }, 500);
                    }
                });
            });
        },

        beforeUpload(file: File) {
            // Valid file is less than 10MB
            const isLt500M = file.size / 1024 / 1024 < 500;
            // If not less than 100MB then notify user and reject upload
            if (!isLt500M) {
                this.$snackbar.image({ message: `Uploading ${file.name} failed. File size can not exceed 100MB.` });
                return false;
            }

            // Check the upload config is still valid
            if (uploadConfigExpired(this.uploadConfig)) {
                this.$snackbar.error("There was an issue uploading a file. Please try again.");
                return this.$apollo.queries.mediaItems.refetch();
            }

            const fileNameAndExtension = splitFileNameAndExtension(file.name);
            const sanitisedFileName = `${slugify(fileNameAndExtension.name)}${fileNameAndExtension.extension}`;
            // Set key and check if file exists
            this.uploadConfig.data.key += `/${sanitisedFileName}`;
            const currentFile = this.mediaItems.find(item => item.type === "FILE" && item.name === sanitisedFileName);
            // Make sure a file with the same name is updated not added
            if (file.type === ZIP_MIME_TYPE) {
                this.extractList[sanitisedFileName] = {
                    tempPath: this.uploadConfig.data.key,
                    fileId: currentFile ? currentFile.id : null
                };
            } else if (currentFile) {
                this.updateList[sanitisedFileName] = {
                    tempPath: this.uploadConfig.data.key,
                    fileId: currentFile.id
                };
            } else {
                this.uploadList[sanitisedFileName] = this.uploadConfig.data.key;
            }
            // Return true to proceed with upload
            return true;
        },

        /* we need this wrapper callback to prevent vue from passing event object as a first param */
        onClose() {
            this.close();
        },

        uploadProgress(e: { percent: number }) {
            bus.$emit("percentLoadEvent", e.percent);
        },

        processUploads() {
            // Get extract, upload and update lists
            const extractList = Object.keys(this.extractList);
            const uploadList = Object.keys(this.uploadList);
            const updateList = Object.keys(this.updateList);
            // Add files if upload list is not empty
            if (uploadList.length) {
                this.addFiles(uploadList);
                this.processing += 1;
            }
            // Update files if update list is not empty
            if (updateList.length) {
                this.updateFiles(updateList);
                this.processing += 1;
            }
            if (extractList.length) {
                this.extractFiles(extractList);
                this.processing += extractList.length;
            }
            // Set loader if processing
            if (this.processing) {
                this.loadPercent = 50;
                bus.$emit("percentLoadEvent", this.loadPercent);
            }
        },

        addFiles(fileList: string[]) {
            // Build files list
            const files = fileList.map(fileName => ({
                name: fileName.replace(/\.[^/.]+$/, ""),
                temporaryPath: this.uploadList[fileName]
            }));
            // Fire files mutation
            this.$apollo
                .mutate({
                    mutation: uploadMediaFilesMutation,
                    variables: {
                        clientId: this.clientId,
                        files,
                        folderId: this.folderId
                    }
                })
                .then(() => {
                    const plural = fileList.length > 1 ? "s" : "";
                    this.$snackbar.success(
                        `File${plural} uploaded`,
                        `${fileList.length} file${plural} successfully added!`
                    );
                })
                .catch(err => {
                    this.$snackbar.error(err.message);
                })
                .finally(() => {
                    this.uploadList = {};
                    this.updateProcessingStatus();
                    this.$apollo.queries.mediaItems.refetch();
                });
        },

        updateFiles(fileList: string[]) {
            // Build files list
            const files = fileList.map(fileName => ({
                name: fileName.replace(/\.[^/.]+$/, ""),
                fileId: this.updateList[fileName].fileId,
                temporaryPath: this.updateList[fileName].tempPath
            }));
            // Fire files mutation
            this.$apollo
                .mutate({
                    mutation: updateMediaFilesMutation,
                    variables: {
                        clientId: this.clientId,
                        folderId: this.folderId,
                        files
                    },
                    refetchQueries: [
                        {
                            query: mediaItemsQuery,
                            variables: {
                                clientId: this.clientId,
                                folderId: this.folderId,
                                orderBy: this.sortBy
                            }
                        }
                    ]
                })
                .then(() => {
                    const plural = fileList.length > 1 ? "s" : "";
                    this.$snackbar.success(
                        `File${plural} updated`,
                        `${fileList.length} file${plural} successfully updated!`
                    );

                    bus.$emit(MediaLibraryAssetEvent.AssetsUpdated, fileList);
                })
                .catch(err => {
                    this.$snackbar.error(err.message);
                })
                .finally(() => {
                    this.updateList = {};
                    this.updateProcessingStatus();
                });
        },

        extractFiles(fileList: string[]) {
            fileList.forEach(fileName => {
                this.$apollo
                    .mutate({
                        mutation: uploadAndExtractZipToFolderMutation,
                        variables: {
                            clientId: this.clientId,
                            folderId: this.folderId,
                            tmpPath: this.extractList[fileName].tempPath
                        }
                    })
                    .then(() => {
                        this.$snackbar.success("Zip extracted", `${fileName} successfully extracted!`);
                    })
                    .catch(err => {
                        this.$snackbar.error(err.message);
                    })
                    .finally(() => {
                        this.extractList = {};
                        this.updateProcessingStatus();
                        this.$apollo.queries.mediaItems.refetch();
                    });
            });
        },

        updateProcessingStatus() {
            this.loadPercent += Math.ceil(50 / this.processing);
            this.loadPercent = this.loadPercent > 100 ? 100 : this.loadPercent;
            bus.$emit("percentLoadEvent", this.loadPercent);
            // Reset values if 100% loaded
            if (this.loadPercent === 100) {
                this.processing = 0;
                this.loadPercent = 0;
            }
        },

        downloadAsset(asset: Asset) {
            if (!asset || !asset.url) {
                return;
            }

            window.open(asset.url, "_blank");
        },

        async downloadFolderById(folderId: string | null) {
            if (!folderId || !this.clientId) {
                return;
            }

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: downloadMediaFolderMutation,
                    variables: {
                        clientId: this.clientId,
                        folderId
                    }
                });

                const url = data && data.downloadMediaFolder;

                if (url) {
                    window.open(url, "_blank");
                } else {
                    this.$snackbar.error("There was an error preparing the folder download.");
                }
            } catch {
                this.$snackbar.error("There was an error preparing the folder download.");
            }
        },

        downloadCurrentFolder() {
            this.downloadFolderById(this.folderId as string | null);
        },

        downloadFolder(asset: Asset) {
            if (!asset || asset.type !== MEDIA_TYPES.FOLDER) {
                return;
            }

            this.downloadFolderById(asset.id);
        },

        copyAssetIdToClipboard(asset: Asset) {
            try {
                const assetString = `:hoxton/${asset.id}/${asset.name}`;
                copyToClipboard([assetString]);
                this.$snackbar.success("Asset reference has been copied to clipboard", assetString);
            } catch {
                this.$snackbar.error("There was an error when trying to copy the reference, please try again.");
            }
        },

        deleteAsset(asset: Asset) {
            this.$Modal.confirm({
                title: "Are you sure?",
                content: `Deleting ${asset.name} will affect any campaign using it.`,
                okText: "Delete",
                onOk: async () => {
                    try {
                        await this.$apollo.mutate({
                            mutation: removeMediaMutation,
                            variables: {
                                clientId: this.clientId,
                                mediaIds: [asset.id]
                            },
                            refetchQueries: [
                                {
                                    query: mediaItemsQuery,
                                    variables: {
                                        clientId: this.clientId,
                                        folderId: this.folderId,
                                        orderBy: this.sortBy
                                    }
                                }
                            ]
                        });
                        // Now that the image is deleted we need to deselect it
                        this.deselectAsset(asset.id);
                        this.$snackbar.success(
                            `${asset.type.charAt(0)}${asset.type.substr(1).toLowerCase()} deleted`,
                            `${asset.name} successfuly deleted!`
                        );

                        bus.$emit(MediaLibraryAssetEvent.AssetRemoved, asset);
                        this.selectActiveFileOnMediaItemsLoad = true;
                        this.$apollo.queries.mediaItems.refetch();
                    } catch (err) {
                        this.$snackbar.unauthorized(err as Error);
                    }
                }
            });
        },

        addSelections() {
            this.close(this.selectedAssets);
        },

        fileIcon(fileName: string) {
            return prettyFileIcons.getIcon(fileName);
        },

        rowClassName(row: { id: string }) {
            return row.id in this.selectedAssets ? "selected" : "";
        },

        toggleAddAssetFromUrlModal() {
            this.addAssetFromUrlModalIsVisible = !this.addAssetFromUrlModalIsVisible;
        },

        toggleGenerateImagesAdvanced() {
            this.generateImagesAdvancedVisible = !this.generateImagesAdvancedVisible;
        },

        assetAddedFromUrl(fileId: string) {
            this.toggleAddAssetFromUrlModal();
            this.$store.dispatch(MediaLibraryAction.SetActiveFile, fileId);
            this.selectActiveFileOnMediaItemsLoad = true;
            this.$apollo.queries.mediaItems.refetch();
        },

        assetAddedFromVeeva(fileId: string) {
            this.changeLibrarySection(LIBRARY_SECTION.IMPORT);
            this.$store.dispatch(MediaLibraryAction.SetActiveFile, fileId);
            this.selectActiveFileOnMediaItemsLoad = true;
            this.$apollo.queries.mediaItems.refetch();
        },

        clearGeneratedResults() {
            this.searches = [];
        },

        onUpdateCustomInputs(customInputs: CustomInputs) {
            this.customInputs = customInputs;

            this.toggleGenerateImagesAdvanced();
        },

        onSearch() {
            this.searchCount += 1;

            const newSearch = {
                searchId: this.searchCount,
                prompt: this.prompt,
                model: this.model,
                ...this.customInputs
            };

            this.searches.unshift(newSearch);
        },

        onUseImage(url: string) {
            this.aIassetUrl = url;
            this.toggleAddAssetFromUrlModal();
        }
    },

    apollo: {
        mediaItems: {
            query: mediaItemsQuery,

            skip() {
                return !this.clientId;
            },

            fetchPolicy: "no-cache",

            variables() {
                return {
                    clientId: this.clientId,
                    folderId: this.folderId,
                    orderBy: this.sortBy
                };
            },

            watchLoading(isLoading) {
                this.isLoading = isLoading;
            },

            result({ data, loading }) {
                // todo: review and fix the bug: https://github.com/Akryum/vue-apollo/issues/492
                // todo: HOX-964
                if (loading) {
                    return;
                }

                if (!data.mediaItems) {
                    this.mediaItems = [];
                } else {
                    this.mediaItemsForDisplay = data.mediaItems;
                }

                if (data.folderBreadCrumbs) {
                    const breadcrumbs = [...data.folderBreadCrumbs];
                    breadcrumbs.shift();
                    this.breadcrumbs = breadcrumbs;
                    this.root = data.folderBreadCrumbs[0].id;
                } else {
                    this.root = null;
                    this.breadcrumbs = [];
                }

                if (data.client) {
                    this.processConfig(data.client.mastheadUploadConfig);
                }
            },

            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        }
    }
});
</script>

<style scoped>
.media-library__upload {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

#media-library {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    outline: 0;
}

#media-library__nav-wrapper {
    display: flex;
    margin-bottom: 10px;
    justify-content: space-between;
}

#media-library__breadcrumbs,
#media-library__change-view {
    font-size: 14px;
}

#media-library__change-view {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-items: center;
}

#media-library__content-wrapper {
    display: flex;
    flex: 1;
    overflow: hidden;
}

#media-library__sidebar {
    width: 25%;
    height: 100%;
    margin-right: 15px;
    position: relative;
}

#media-library__upload-area {
    display: flex;
    flex-direction: column;
    width: 99%;
    height: 100%;
    overflow: hidden;
}

.media-library__upload-area-placeholder {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    height: 100%;
}

#media-library__data {
    width: 75%;
    overflow: hidden scroll;
}

#media-library__data.full-width {
    width: 100%;
}

.media-library__upload {
    height: 100%;
}

.media-library__sort {
    margin-left: auto;
    margin-right: 20px;
}

#media-library :deep(.media-library__asset-icon) {
    margin-right: 8px;
}

.media-library__asset-icon {
    margin-right: 8px;
}

.media-library__data-card {
    cursor: pointer;
}

.media-library__data-card-icon,
.media-library__data-card-image {
    width: 100%;
    height: 125px;
}

.media-library__data-card-icon {
    font-size: 64px;
    padding: 34px 0;
}

.media-library__data-card-image {
    background: #f6f6f6 no-repeat center center;
    background-size: contain;
}

.media-library__data-card-name {
    text-transform: uppercase;
    text-align: center;
    padding: 5px;
    font-size: 12px;
    border-top: 1px solid #e9e9e9;
    text-overflow: ellipsis;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    min-height: 40px;
}

.media-library__data-card-delete-button {
    position: absolute;
    z-index: 10;
    margin: 3px;
    bottom: 0;
    opacity: 0;
    font-size: 20px;
    padding: 0 5px;
}

.media-library__data-card-download-button {
    position: absolute;
    z-index: 10;
    margin: 3px;
    bottom: 0;
    right: 34px;
    opacity: 0;
    font-size: 20px;
    padding: 0 5px;
}

.media-library__data-card-download-button--solo {
    right: 0;
}

.media-library__data-card-copy-button {
    position: absolute;
    z-index: 10;
    margin: 3px;
    bottom: 0;
    right: 0;
    opacity: 0;
    font-size: 20px;
    padding: 0 5px;
}

.media-library__data-card-rename-button {
    position: absolute;
    z-index: 10;
    margin: 3px;
    bottom: 0;
    right: 0;
    opacity: 0;
    font-size: 20px;
    padding: 0 5px;
}

.media-library__data-card-delete-button .ivu-icon-ios-trash-outline {
    font-size: 20px;
}

#media-library :deep(.media-library__asset-delete-button) {
    position: absolute;
    right: 5px;
    top: 50%;
    margin-top: -17px;
    opacity: 0;
}

#media-library :deep(.media-library__asset-rename-button),
#media-library :deep(.media-library__asset-download-button) {
    position: absolute;
    right: 50px;
    top: 50%;
    margin-top: -17px;
    opacity: 0;
}

#media-library :deep(.media-library__asset-download-button--solo) {
    right: 5px;
}

#media-library :deep(.ivu-table-cell) {
    position: relative;
    padding: 4px 18px;
}

#media-library :deep(.ivu-table-row:hover .media-library__asset-delete-button),
#media-library :deep(.ivu-table-row:hover .media-library__asset-rename-button),
#media-library :deep(.ivu-table-row:hover .media-library__asset-download-button),
#media-library :deep(.ivu-table-row:hover .media-library__data-card-copy-button),
#media-library :deep(.ivu-table-row:hover .media-library__data-card-rename-button),
.media-library__data-card:hover .media-library__data-card-delete-button,
.media-library__data-card:hover .media-library__data-card-copy-button,
.media-library__data-card:hover .media-library__data-card-download-button,
.media-library__data-card:hover .media-library__data-card-rename-button,
.media-library__data-card:hover .media-library__data-card-delete-button:hover,
.media-library__data-card:hover .media-library__data-card-rename-button:hover,
.media-library__data-card:hover .media-library__data-card-copy-button:hover,
.media-library__data-card:hover .media-library__data-card-download-button:hover {
    opacity: 1;
    transition: none;
}

.media-library__data-card:hover .media-library__data-card-copy-button,
.media-library__data-card:hover .media-library__data-card-rename-button {
    color: #0092ff;
    border-color: #0092ff;
}

.media-library-modal-footer__content {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.media-library-modal-footer__status {
    flex: 1 0 0;
    padding: 5px 0;
    text-align: left;
}

.media-library__folder-preview-wrapper {
    display: block;
    height: 125px;
    text-align: center;
}

.folder-preview.media-library__data-card-image {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 125px;
    margin: auto;
    background: white;
}

.file-format-icon {
    padding: 25px 0;
    display: block;
}
</style>

<style lang="scss">
@use "@style/variables" as *;

$icons: "7z" "aac" "ai" "archive" "arj" "audio" "avi" "css" "csv" "dbf" "doc" "dwg" "exe" "fla" "flac" "gif" "html"
    "iso" "jpg" "js" "json" "mdf" "mp2" "mp3" "mp4" "mxf" "nrg" "pdf" "png" "ppt" "psd" "rar" "rtf" "svg" "text" "tiff"
    "txt" "unknown" "video" "wav" "wma" "xls" "xml" "zip";

@each $icon in $icons {
    .pretty-file-icon-#{$icon} {
        background: url("@/packages/pretty-file-icons/svg/#{$icon}.svg") no-repeat center;
        display: block;
        height: 100%;
        width: 100%;
    }
}

#media-library {
    &__data.full-width {
        .media-library__data-col {
            width: 16.66%;
        }
    }

    .media-library {
        &__nav-wrapper {
            &--client-name {
                font-weight: bold;
                margin: 0 $spacing-base;
            }
        }

        &__upload {
            .ivu-upload-drag {
                flex: 1;
                height: 100%;
                width: 100%;
                display: flex;
            }

            &-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                width: 100%;

                &--text {
                    font-size: 14px;
                    padding: $spacing-smaller;
                }

                &--icon {
                    font-size: 64px;
                }
            }
        }

        &__asset-name-column {
            line-height: 47px;

            .media-library__asset-delete-button,
            .media-library__asset-rename-button {
                font-size: $ios-trash-outline-fs;
                margin-top: -$ios-trash-outline-fs;
            }

            .media-library__asset-download-button {
                font-size: $ios-trash-outline-fs;
                margin-top: -$ios-trash-outline-fs;
            }

            .media-library__asset-download-button .ivu-icon {
                font-size: $ios-trash-outline-fs;
            }
        }

        &__data-row {
            padding: 0 4px 0;
            margin: 0 !important;
        }

        &__data-row-empty {
            display: flex;
            justify-content: space-around;
            height: 100%;
            align-items: center;
            padding-left: $spacing-small;
        }

        &__data-col {
            padding: 0 $spacing-small $spacing-small;
            width: 25%;
            margin: 0 0 $spacing-small;

            &.selected .ivu-card-bordered {
                border-color: var(--wpp-primary-color-500);
            }

            &:nth-child(1n) {
                padding-left: 2px;
            }

            &:nth-child(4n) {
                padding-right: 2px;
            }
        }

        &__asset-tag-list {
            &.slide-enter-active,
            &.slide-leave-active {
                transition: left 0.5s;
            }

            &.slide-enter,
            &.slide-leave-to {
                left: -100%;
            }
        }
    }

    .media-library {
        &__action-tabs {
            border-bottom: 1px solid #5e00b5;
            margin: 10px 0;
            display: flex;
            justify-content: center;
            span {
                display: inline-block;
                text-transform: capitalize;
                padding: 8px 15px;
                border-top-left-radius: 8px;
                border-top-right-radius: 8px;
                margin: 0 5px;
                border: none;
                color: #5e00b5;
                font-weight: 500;
                font-size: 14px;
                cursor: pointer;
                &.active {
                    background: #5e00b5;
                    color: #fff;
                }
            }
        }
        &__upload-content {
            min-height: 40vh;
            .ivu-btn {
                width: 100%;
            }
        }

        &__imagine-content {
            display: flex;
            flex-direction: column;
            gap: 20px;

            label {
                display: block;
                width: 100%;
            }
            textarea {
                min-height: 40vh;
            }
        }

        &__imagine-model {
            font-size: 14px;
            border-radius: 8px;
            padding: 4px 10px;
            border: 1px solid rgb(139, 145, 154);
            color: #333;
            width: 150px;
            a {
                display: inline-block;
                width: 100%;
                .ivu-icon {
                    float: right;
                }
            }
        }

        &__imagine-results {
            background: #f8f9fb;
            border: 1px solid #e7eaee;
            box-shadow:
                0px 0px 1px 0px rgba(52, 58, 63, 0.1),
                0px 1px 5px 0px rgba(52, 58, 63, 0.1);
            border-radius: 12px;
            margin-bottom: 20px;
            padding: 16px 8px 0;
            min-height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            &-clear {
                margin: 0 auto 10px 8px;
            }
            .search-results__header {
                padding: 0 8px;
            }
            .search-results-previews {
                display: flex;
            }
            .search-results-preview {
                margin: 8px;
            }
            .alert__content {
                h3 {
                    font-size: 12px;
                    font-weight: 400;
                }
            }
        }
        &__imagine-no-results-title {
            display: inline-block;
            padding: 20px 0;
            text-align: center;
            width: 100%;
        }
    }

    &__breadcrumbs {
        .home-icon {
            font-size: 18px;
        }
    }

    &__change-view .ivu-icon {
        font-size: 24px;
        font-weight: bold;
    }

    &__data {
        overflow: hidden auto;

        .ivu-card-body {
            padding: 0;

            video {
                display: block;
            }
        }
    }

    .assetsTable {
        tr.selected td {
            background-color: $table-td-hover-bg;
        }
    }
}

.media-library-modal-footer__button {
    margin: 0 0 0 $spacing-small;
}

.media-library-modal-footer__button--sidebar {
    margin: 0 0 $spacing-small;
}
</style>
